/**
 * @file PublicDeletionPreview.tsx
 * @brief DEV ONLY: the public delete-account pages with a stand-in backend
 *        (`/__preview-public/delete-account`, `/__preview-public/delete-account/confirm?token=…`).
 *
 * @details
 * The three B7.3 calls are answered in the page (no fetch leaves it).
 * Tokens: `personal`, `company`, anything else = 410 LINK_INVALID.
 * Emails: `limit@…` = 429; `fail@…` = network error; others = 200.
 * Mounted only when `import.meta.env.DEV` (routes/index.tsx).
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { createPublicDeletionApi } from "../publicDeletion";
import { DeleteAccountConfirmPage, DeleteAccountPublicPage } from "../DeleteAccountPublicPage";

const json = (status: number, body: unknown) =>
  new Promise<Response>((r) => setTimeout(() => r(new Response(JSON.stringify(body), { status })), 600));

const stubApi = createPublicDeletionApi("", async (url, init) => {
  const body = init?.body ? (JSON.parse(String(init.body)) as { email?: string; token?: string }) : {};
  if (url.includes("/confirm?token=")) {
    const token = decodeURIComponent(url.split("token=")[1] ?? "");
    if (token === "personal" || token === "company") {
      return json(200, {
        success: true,
        accountType: token,
        emailMasked: "s***@home.test",
        expiresAt: new Date(Date.now() + 864e5).toISOString(),
        whatHappens:
          token === "company"
            ? "Your account belongs to your company. Confirming sends a deletion request to your company administrator and VERBLIZR support. It is completed within 30 days."
            : "Confirming deletes your account, your meetings, notes and recordings, and stops any plan. Everything is erased within 30 days. This can't be undone.",
      });
    }
    return json(410, { success: false, code: "LINK_INVALID", message: "This link has expired or has already been used. Please ask for a new one." });
  }
  if (url.endsWith("/confirm")) {
    if (body.token === "company") {
      return json(200, { success: true, code: "DELETION_REQUESTED", message: "We've sent your request to your company administrator and VERBLIZR support. Your account will be deleted within 30 days.", dueAt: null });
    }
    return json(200, { success: true, code: "DELETION_STARTED", message: "We've started deleting your account; it will be done within 30 days.", erasureDueAt: null });
  }
  if (body.email?.startsWith("fail@")) throw new Error("offline");
  if (body.email?.startsWith("limit@")) {
    return json(429, { success: false, code: "RATE_LIMITED", message: "You've asked a few times in the last hour. Please try again later." });
  }
  return json(200, { success: true, code: "CHECK_YOUR_EMAIL", message: "If a VERBLIZR account uses this email, we've sent it a link to confirm. The link works for 24 hours." });
});

export function PublicDeletionPreviewPage() {
  return <DeleteAccountPublicPage api={stubApi} />;
}

export function PublicDeletionConfirmPreviewPage() {
  return <DeleteAccountConfirmPage api={stubApi} />;
}
